import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CircleUserRoundIcon } from 'lucide-react';
import { useModal } from './ModalContext';
interface HeaderProps {
  isStatic?: boolean;
}
const demoButtonClasses = 'inline-flex min-h-11 items-center justify-center rounded-xl bg-primary-700 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-800 focus-visible:ring-primary-600';
const accountLinkClasses = 'inline-flex min-h-11 items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:text-primary-700 focus-visible:ring-primary-600';
export const Header = ({
  isStatic = false
}: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogo, setShowLogo] = useState(isStatic);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const {
    openTalkToUs
  } = useModal();
  useEffect(() => {
    const handleScroll = () => {
      if (!isStatic) {
        const heroLogo = document.getElementById('hero-logo');
        if (heroLogo) setShowLogo(heroLogo.getBoundingClientRect().bottom < 0);
      }
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isStatic]);
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);
  const handleNavigation = (event: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    event.preventDefault();
    setIsMenuOpen(false);
    if (isHomePage) {
      document.getElementById(anchor)?.scrollIntoView({
        behavior: 'smooth'
      });
      return;
    }
    navigate({
      pathname: '/',
      hash: `#${anchor}`
    });
  };
  const anchorItems = [{
    name: 'Why Nisa',
    anchor: 'the-problem'
  }];
  const pageItems = [{
    name: 'Schools',
    to: '/schools'
  }, {
    name: 'About',
    to: '/about'
  }];
  return <>
      <header className={`sticky top-0 z-40 w-full border-b transition-all duration-200 ${isScrolled || isStatic ? 'border-gray-100 bg-white/95 shadow-sm backdrop-blur-md' : 'border-transparent bg-white/85 backdrop-blur-sm'}`} data-id="element-114" data-mp="1mkrxforyc1lb">
        <div className="container flex h-16 items-center justify-between md:h-20" data-id="element-115" data-mp="yp5p3vnun1op">
          <Link to="/" aria-label="Nisa home" className={`h-7 transition-all duration-200 md:h-8 ${showLogo || isStatic || !isHomePage ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'}`} data-id="element-116" data-mp="yr8qfbugch8q">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-full w-auto" data-id="element-117" data-mp="2y1rn0cmx02bn" />
          </Link>

          <nav className="hidden items-center gap-7 md:flex" aria-label="Primary navigation" data-id="element-118" data-mp="mcbpwxnwlfbj">
            {anchorItems.map(item => <a key={item.anchor} href={`/#${item.anchor}`} onClick={event => handleNavigation(event, item.anchor)} className="text-sm font-medium text-gray-600 transition-colors hover:text-primary-700" data-id="element-119" data-mp="w47qqghdjjjg">
                {item.name}
              </a>)}
            {pageItems.map(item => {
            const isCurrent = location.pathname === item.to;
            return <Link key={item.to} to={item.to} aria-current={isCurrent ? 'page' : undefined} className={`text-sm font-medium transition-colors hover:text-primary-700 ${isCurrent ? 'text-primary-700 underline decoration-primary-400 decoration-2 underline-offset-[6px]' : 'text-gray-600'}`} data-id="element-120" data-mp="105t9giglas1k">
                  {item.name}
                </Link>;
          })}
            <span className="h-5 w-px bg-gray-200" aria-hidden="true" data-id="element-121" data-mp="3bw5f83x2zra2" />
            <a href="https://app.nisa.coach/" className={accountLinkClasses} data-id="element-122" data-mp="22yno3tszf9hz">
              <CircleUserRoundIcon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" data-id="element-123" data-mp="230rpo53erwf3" />
              Log in
            </a>
            <button type="button" onClick={openTalkToUs} className={demoButtonClasses} aria-haspopup="dialog" data-id="element-124" data-mp="2cwbkeo68kp29">
              Request a demo
            </button>
          </nav>

          <button className="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50 md:hidden" onClick={() => setIsMenuOpen(open => !open)} aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMenuOpen} aria-controls="mobile-navigation" data-id="element-125" data-mp="1k85y3wzjas0b">
            <span className="relative flex h-5 w-5 items-center justify-center" data-id="element-126" data-mp="1y0m2b1468w2t">
              <span className={`absolute h-0.5 w-5 bg-current transition-transform ${isMenuOpen ? 'rotate-45' : '-translate-y-1.5'}`} data-id="element-127" data-mp="11ihhza0cdg50" />
              <span className={`absolute h-0.5 w-5 bg-current transition-opacity ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} data-id="element-128" data-mp="15hq884w6avvt" />
              <span className={`absolute h-0.5 w-5 bg-current transition-transform ${isMenuOpen ? '-rotate-45' : 'translate-y-1.5'}`} data-id="element-129" data-mp="xxe8q5y64yrw" />
            </span>
          </button>
        </div>
      </header>

      <div id="mobile-navigation" className={`fixed inset-x-0 bottom-0 top-16 z-30 bg-white transition-all duration-300 md:hidden ${isMenuOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-3 opacity-0'}`} data-id="element-130" data-mp="p0ww0ya5osu1">
        <nav className="container flex h-full flex-col overflow-y-auto py-8" aria-label="Mobile navigation" data-id="element-131" data-mp="a9o1carecha1">
          <div className="space-y-1" data-id="element-132" data-mp="5abcxlkvn25c">
            {anchorItems.map(item => <a key={item.anchor} href={`/#${item.anchor}`} onClick={event => handleNavigation(event, item.anchor)} className="block rounded-xl px-4 py-3 text-xl text-cyan-950 transition-colors hover:bg-primary-50" data-id="element-133" data-mp="19htb4egw9i9">
                {item.name}
              </a>)}
            {pageItems.map(item => {
            const isCurrent = location.pathname === item.to;
            return <Link key={item.to} to={item.to} aria-current={isCurrent ? 'page' : undefined} className={`block rounded-xl px-4 py-3 text-xl transition-colors hover:bg-primary-50 ${isCurrent ? 'bg-primary-50 text-primary-700' : 'text-cyan-950'}`} data-id="element-134" data-mp="3drskuh56st3n">
                  {item.name}
                </Link>;
          })}
          </div>

          <div className="mt-auto border-t border-gray-100 px-4 pt-6" data-id="element-135" data-mp="e7d7ai1bzjg">
            <button type="button" onClick={() => {
            setIsMenuOpen(false);
            openTalkToUs();
          }} className={`${demoButtonClasses} w-full`} aria-haspopup="dialog" data-id="element-136" data-mp="ez8slbmtx9q">
              Request a demo
            </button>
            <a href="https://app.nisa.coach/" className={`${accountLinkClasses} mt-3 w-full`} data-id="element-137" data-mp="3rslfr2zehq7a">
              <CircleUserRoundIcon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" data-id="element-138" data-mp="jdbj4aea5d6w" />
              Log in
            </a>
          </div>
        </nav>
      </div>
    </>;
};